import { useLocation, useNavigate } from 'react-router-dom';
import { useEntitlementContext } from '@/providers/EntitlementProvider';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CreditCard, Clock, ExternalLink } from 'lucide-react';
import { useBillingMutations } from '@/hooks/billing';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Routes that are exempt from subscription gating.
 * These remain accessible regardless of subscription status.
 */
const EXEMPT_ROUTE_PREFIXES = [
  '/business-settings/billing',
  '/settings',
  '/pricing',
];

function isExemptRoute(pathname: string): boolean {
  return EXEMPT_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

/**
 * Gate component that blocks access to product features when
 * the company has no active subscription.
 * 
 * Renders children normally for full_access and grace_period states.
 * Shows paywall for no_subscription and restricted states.
 * Shows grace period banner for grace_period state.
 */
export function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { enforcementState, isLoading, isGracePeriod, graceDeadline, isRestricted, source, refetch } = useEntitlementContext();
  const location = useLocation();
  const accessCodeChecked = useRef(false);

  // Check for ?success=true from Stripe checkout return — force refetch
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      refetch();
    }
  }, [location.search, refetch]);

  // Post-login access code confirmation — runs once when entitlement loads
  useEffect(() => {
    if (isLoading || accessCodeChecked.current) return;
    accessCodeChecked.current = true;

    supabase.auth.getUser().then(({ data }) => {
      const code = data.user?.user_metadata?.access_code;
      if (!code) return; // No code was entered during signup

      if (source === 'override') {
        toast.success('Your access code has been applied successfully!');
      } else {
        toast.warning('Your access code could not be applied. Please contact support or subscribe to a plan.');
      }

      // Clear access_code from metadata so the toast doesn't fire again
      supabase.auth.updateUser({ data: { access_code: null } });
    });
  }, [isLoading, source]);

  // Exempt routes bypass the gate entirely
  if (isExemptRoute(location.pathname)) {
    return <>{children}</>;
  }

  // While loading entitlement, show loading spinner (fail-closed: don't show content)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying subscription...</p>
        </div>
      </div>
    );
  }

  // Restricted or no subscription — show paywall
  if (isRestricted) {
    return <PaywallView enforcementState={enforcementState} source={source} onRefresh={refetch} />;
  }

  // Grace period — show children with persistent warning banner
  if (isGracePeriod) {
    return (
      <>
        <GracePeriodBanner graceDeadline={graceDeadline} />
        {children}
      </>
    );
  }

  // full_access — render normally
  return <>{children}</>;
}

/**
 * Paywall view shown when the company has no active subscription.
 */
function PaywallView({ 
  enforcementState, 
  source,
  onRefresh 
}: { 
  enforcementState: string;
  source: string;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const { createPortalSession } = useBillingMutations();
  // Only show "Reactivate" for companies that previously had a Stripe subscription
  const isReactivation = enforcementState === 'restricted' && source === 'stripe';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <CreditCard className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {isReactivation ? 'Subscription Inactive' : 'Subscription Required'}
          </CardTitle>
          <CardDescription className="text-base">
            {isReactivation 
              ? 'Your subscription has been canceled or expired. Reactivate to regain access to all features.'
              : 'An active subscription is required to access this application. Choose a plan to get started.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isReactivation ? (
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => createPortalSession.mutate()}
              disabled={createPortalSession.isPending}
            >
              {createPortalSession.isPending ? 'Loading...' : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Reactivate Subscription
                </>
              )}
            </Button>
          ) : (
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => navigate('/pricing')}
            >
              View Plans & Subscribe
            </Button>
          )}
          <Button 
            variant="ghost" 
            className="w-full" 
            size="sm"
            onClick={onRefresh}
          >
            I just paid — refresh status
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Persistent banner shown during the grace period after a payment failure.
 */
function GracePeriodBanner({ graceDeadline }: { graceDeadline: string | null }) {
  const { createPortalSession } = useBillingMutations();
  
  let daysRemaining = 0;
  if (graceDeadline) {
    const deadline = new Date(graceDeadline);
    const now = new Date();
    daysRemaining = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className="bg-destructive/10 border-b border-destructive/20 px-4 py-3">
      <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
          <span className="text-destructive font-medium">
            Payment failed.
          </span>
          <span className="text-muted-foreground">
            {daysRemaining > 0 
              ? `You have ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} to resolve this before access is restricted.`
              : 'Your grace period has expired.'
            }
          </span>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => createPortalSession.mutate()}
          disabled={createPortalSession.isPending}
          className="flex-shrink-0"
        >
          <Clock className="mr-1 h-3 w-3" />
          Update Payment
        </Button>
      </div>
    </div>
  );
}